type ComputeVisibleTokenCountArgs = Readonly<{
  /** Inner content width available to the token row, in pixels. */
  containerWidth: number
  /** Rendered width of each token, in settings order, in pixels. */
  tokenWidths: ReadonlyArray<number>
  /** Horizontal gap between adjacent tokens (and before the badge), in pixels. */
  gap: number
  /** Reserved width for the `+N more` overflow badge, in pixels. */
  badgeWidth: number
}>

export type VisibleTokenCount = Readonly<{visibleCount: number; hiddenCount: number}>

/**
 * Decide how many multi-select tokens fit on a single row before an overflow
 * `+N more` badge is needed.
 *
 * Behavior:
 * - If every token fits within `containerWidth`, all are shown and no badge is
 *   needed.
 * - Otherwise tokens are filled left-to-right at full size, always reserving
 *   room for the badge, and whatever does not fit is rolled into the badge.
 * - The row never collapses to zero tokens: when there is overflow at least one
 *   token is always shown (the caller is expected to let that single token be
 *   clipped/truncated when even one full token cannot fit).
 *
 * Pure and deterministic: every measurement is an argument, so the fit logic is
 * unit-tested without a DOM.
 */
export function computeVisibleTokenCount({
  containerWidth,
  tokenWidths,
  gap,
  badgeWidth,
}: ComputeVisibleTokenCountArgs): VisibleTokenCount {
  const total = tokenWidths.length
  if (total === 0) return {visibleCount: 0, hiddenCount: 0}

  // Does everything fit with no badge? Sum the token widths plus the gaps
  // between them.
  const totalWidth = tokenWidths.reduce((sum, width) => sum + width, 0) + gap * (total - 1)
  if (totalWidth <= containerWidth) {
    return {visibleCount: total, hiddenCount: 0}
  }

  // Overflow: reserve space for the badge (and the gap before it), then greedily
  // fit tokens from the left.
  const budget = containerWidth - badgeWidth - gap
  let used = 0
  let visibleCount = 0
  for (let i = 0; i < total; i++) {
    const width = tokenWidths[i] ?? 0
    const next = used === 0 ? width : used + gap + width
    if (next > budget) break
    used = next
    visibleCount++
  }

  // Never show zero tokens on overflow; the caller truncates the single token.
  if (visibleCount === 0) visibleCount = 1

  return {visibleCount, hiddenCount: total - visibleCount}
}
